import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ImagePlus, Rocket, Save, Trash2 } from 'lucide-react'
import { getCompany, saveCompany, uploadLogo, removeLogo, getConfig, saveConfig } from '../../api/settings'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import TimeZoneSelect from '../../components/TimeZoneSelect'
import HelpTip from '../../components/HelpTip'
import { useToast } from '../../components/Toast.jsx'
import { useSession } from '../../session'
import { currencyCode } from '../../utils/format'
import { Field } from '../master/masterShared'
import { PracticeTabs } from '../practice/practiceShared'
import { SettingsHeader, SettingsTabs } from './settingsShared'

const isOn = (v) => [true, 1, '1', 'true', 'yes', 'on'].includes(v)

/** "NGN · Nigerian Naira" */
const currencyLabel = (code) => {
  try { return `${code} · ${new Intl.DisplayNames(['en'], { type: 'currency' }).of(code)}` } catch { return code }
}

const ZONE_HINT = {
  saved: 'Dates and times on sales, receipts and journal entries follow this clock.',
  currency: 'Set from your currency. Change it if your business keeps another clock.',
  none: 'Not chosen yet, so dates follow UTC. Choose the time zone your business is in.',
}

/** The business's name, contact details and logo, and how stock is tracked.
    With `practice`, an accounting practice's settings: name, contact details
    and logo only (it has no books). */
export default function CompanySettings({ practice = false }) {
  const { refreshSettings, has, roles = [] } = useSession()
  const { notify } = useToast()
  const fileInput = useRef(null)
  const [loading, setLoading] = useState(true)
  const [companyId, setCompanyId] = useState('')
  const [form, setForm] = useState({ company_name: '', tagline: '', address: '', phone: '', email: '', website: '', logo_path: '' })
  const [preview, setPreview] = useState('')
  const [binTracking, setBinTracking] = useState(false)
  const [measurements, setMeasurements] = useState(false)
  const [zone, setZone] = useState('')
  const [zoneSource, setZoneSource] = useState('')
  const [zoneTouched, setZoneTouched] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState('')
  const [nameError, setNameError] = useState('')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const d = await getCompany()
        if (d) {
          setCompanyId(d.company_id != null ? String(d.company_id) : '')
          setForm({ company_name: d.company_name || '', tagline: d.tagline || '', address: d.address || '', phone: d.phone || '', email: d.email || '', website: d.website || '', logo_path: d.logo_path || '' })
          setPreview(d.logo_path || '')
        }
      } catch (e) { setError(e.message || 'Couldn’t load your company details.') }
      try {
        const c = await getConfig()
        setBinTracking(isOn(c?.bin_tracking))
        setMeasurements(c?.measurements === true)
        setZone(c?.time_zone || '')
        setZoneSource(c?.time_zone_source || '')
      } catch {}
      setLoading(false)
    })()
  }, [])

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }))

  // Tailoring and fashion (customer measurements): saved as soon as it's switched.
  const switchMeasurements = async (on) => {
    setSwitching(true)
    setError('')
    setMeasurements(on)
    try {
      await saveConfig({ measurements: on })
      await refreshSettings()
      notify(on ? 'Customer measurements on' : 'Customer measurements off')
    } catch (e) {
      setMeasurements(!on)
      setError(e.message || 'Couldn’t change that setting.')
    } finally {
      setSwitching(false)
    }
  }

  const pickLogo = async (file) => {
    if (!file) return
    setError(''); setUploading(true)
    try {
      const res = await uploadLogo(file)
      set('logo_path', res.logo_path)
      const base = import.meta.env.VITE_API_URL || ''
      setPreview((res.logo_path || '').startsWith('http') ? res.logo_path : (base + res.logo_path))
      await refreshSettings()
      notify('Logo uploaded')
    } catch (e) { setError(e.message || 'Couldn’t upload the logo.') }
    setUploading(false)
    if (fileInput.current) fileInput.current.value = ''
  }

  const dropLogo = async () => {
    if (!window.confirm('Remove the current logo?')) return
    setError('')
    try {
      await removeLogo()
      set('logo_path', '')
      setPreview('')
      await refreshSettings()
      notify('Logo removed')
    } catch (e) { setError(e.message || 'Couldn’t remove the logo.') }
  }

  const save = async () => {
    setError(''); setNameError('')
    if (!form.company_name.trim()) { setNameError(practice ? 'Give your practice a name.' : 'Give your company a name.'); return }
    setSaving(true)
    try {
      await saveCompany(form)
      if (!practice && zoneTouched && zone) {
        await saveConfig({ time_zone: zone })
        setZoneSource('saved')
        setZoneTouched(false)
      }
      await refreshSettings()
      notify(practice ? 'Practice details saved' : 'Company details saved')
    } catch (e) { setError(e.message || 'Couldn’t save your company details.') }
    setSaving(false)
  }

  return (
    <div className="ci-page">
      {practice ? <PracticeTabs /> : <SettingsTabs />}
      {practice ? (
        <SettingsHeader title="Settings" meta={`Your practice’s name, contact details and logo.${companyId ? ` Company ID ${companyId}.` : ''}`} />
      ) : (
        <SettingsHeader
          title="Company"
          meta={`Your business name, contact details and logo, used on receipts and invoices.${companyId ? ` Company #${companyId}.` : ''}`}
          actions={<Link className="ci-btn ci-btn-ghost" to="/onboarding"><Rocket size={15} />Getting started</Link>}
        />
      )}
      {error && <ErrorBanner message={error} />}

      {loading ? <Spinner label="Loading your company details…" /> : (
        <div className="set-stack">
          <div className="ci-form-meta-card mf-form">
            <div className="mf-card-title">{practice ? 'Practice details' : 'Company details'}</div>
            <div className="ci-form-meta-top two">
              <Field label={practice ? 'Practice name' : 'Company name'} error={nameError} hint={practice ? 'Your clients see it under Settings → Accountant and in their Users list.' : undefined}>
                <input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} />
              </Field>
              {!practice && (
                <Field label="Tagline (optional)" hint="A short line under your name on receipts and invoices.">
                  <input value={form.tagline} onChange={(e) => set('tagline', e.target.value)} />
                </Field>
              )}
            </div>
            <div className="ci-form-meta-top two">
              <Field label="Address"><input value={form.address} onChange={(e) => set('address', e.target.value)} /></Field>
              <Field label="Phone"><input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
            </div>
            <div className="ci-form-meta-top two">
              <Field label="Email"><input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
              <Field label="Website (optional)"><input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder={practice ? 'e.g. www.yourfirm.com' : 'e.g. www.yourshop.com'} /></Field>
            </div>
            {!practice && (
              <div className="ci-form-meta-top two">
                <Field label="Currency" hint="Every amount is shown in this currency. ASYS sets it for you; contact us to change it.">
                  <input value={currencyLabel(currencyCode())} readOnly disabled />
                </Field>
                <Field label="Time zone" hint={zoneTouched ? 'Saved with the rest. Dates and times recorded from then on follow it.' : ZONE_HINT[zoneSource] || ZONE_HINT.saved}>
                  <TimeZoneSelect value={zone} onChange={(z) => { setZone(z); setZoneTouched(true) }} aria-label="Time zone" />
                </Field>
              </div>
            )}
          </div>

          <div className="ci-form-meta-card mf-form">
            <div className="mf-card-title">Logo</div>
            <div className="set-logo">
              <div className="set-logo-box">
                {preview ? <img src={preview} alt={practice ? 'Your practice’s logo' : 'Your company logo'} /> : <span>No logo yet</span>}
              </div>
              <div className="set-logo-side">
                <p className="doc-hint">{practice ? 'Your clients see it next to your practice’s name under Settings → Accountant.' : 'Shown at the top of the Overview and on receipts and invoices.'} PNG, JPG, GIF or WebP.</p>
                <div className="set-actions">
                  <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={(e) => pickLogo(e.target.files?.[0])} />
                  <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={uploading} onClick={() => fileInput.current?.click()}>
                    <ImagePlus size={14} />{uploading ? 'Uploading…' : (preview ? 'Change logo' : 'Upload logo')}
                  </button>
                  {!!(form.logo_path || preview) && (
                    <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm ci-btn-danger" onClick={dropLogo}><Trash2 size={14} />Remove</button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {!practice && <div className="ci-form-meta-card mf-form">
            <div className="mf-card-title">Stock</div>
            <p className="doc-hint">
              <strong>Stock by bin: {binTracking ? 'on' : 'off'}.</strong>{' '}
              {binTracking
                ? 'Stock is kept by bin (a shelf or storage area) within each location.'
                : 'Stock is kept per location only.'}{' '}
              This is part of your plan: change it under <Link to="/settings/plan">Plan &amp; billing</Link>.
            </p>
          </div>}

          {!practice && has('jobs') && (
            <div className="ci-form-meta-card mf-form">
              <div className="mf-card-title">Tailoring and fashion</div>
              <label className="ci-form-toggle msr-toggle">
                <input type="checkbox" checked={measurements} disabled={switching || !roles.includes('admin')} onChange={(e) => switchMeasurements(e.target.checked)} />
                <span>
                  <strong>Keep customer measurements</strong>{' '}
                  <HelpTip text="For tailors and fashion designers. Each customer gets a Measurements card for sets such as chest, waist and sleeve, and each job shows the measurements it’s made to. Turning it off hides them; nothing is deleted." />
                  <span className="cell-sub">For tailors and fashion designers who sew to a customer’s measurements.</span>
                </span>
              </label>
            </div>
          )}

          <div className="ci-form-action-bar">
            <div className="ci-form-action-left">
              <button type="button" className="ci-btn ci-btn-primary" disabled={saving || uploading} onClick={save}><Save size={14} />{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
